// Amplitude Integration
import { openTidioChatWithMessage } from '../tidio';

type AmplitudeInitOptions = {
    sampleRate?: number;
    autocapture?: { elementInteractions?: boolean };
};

const initializeAmplitude = (userId?: string, options?: AmplitudeInitOptions) => {
    // guard against double initialization
    const w = window as unknown as Record<string, any>;
    // simple on-ready callback queue for events fired before SDK loads
    w.__amplitudeOnReady = w.__amplitudeOnReady || [];
    if (w.__amplitudeInitialized) {
        // If already initialized, still set userId if provided
        try {
            if (userId && w.amplitude && typeof w.amplitude.setUserId === 'function') {
                w.amplitude.setUserId(userId);
            }
            // flush any pending callbacks
            if (Array.isArray(w.__amplitudeOnReady)) {
                const callbacks: Array<() => void> = w.__amplitudeOnReady.splice(0);
                callbacks.forEach((cb) => {
                    try {
                        cb();
                    } catch {
                        // no-op
                    }
                });
            }
        } catch {
            // no-op
        }
        return;
    }

    const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY as string;
    if (!apiKey) {
        // [Amplitude] REACT_APP_AMPLITUDE_API_KEY is missing. Skipping init.
        return;
    }
    const analyticsSrc = 'https://cdn.amplitude.com/libs/analytics-browser-2.11.1-min.js.gz';
    const sessionReplaySrc = 'https://cdn.amplitude.com/libs/plugin-session-replay-browser-1.8.0-min.js.gz';
    const engagementSrc = `https://cdn.amplitude.com/script/${apiKey}.engagement.js`;

    const insertBeforeFirstScript = (script: HTMLScriptElement) => {
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript?.parentNode?.insertBefore(script, firstScript);
    };

    // load core analytics browser
    const analyticsScript = document.createElement('script');
    analyticsScript.async = true;
    analyticsScript.src = analyticsSrc;

    analyticsScript.onload = () => {
        // load session replay plugin after core is available
        const sessionScript = document.createElement('script');
        sessionScript.async = true;
        sessionScript.src = sessionReplaySrc;
        sessionScript.onload = () => {
            try {
                const amplitude = (w as any).amplitude;
                const sessionReplay = (w as any).sessionReplay;

                if (amplitude && sessionReplay && typeof amplitude.add === 'function') {
                    const sampleRate = options?.sampleRate ?? 1;
                    amplitude.add(sessionReplay.plugin({ sampleRate }));
                }
                // initialize amplitude with apiKey, userId and options
                if (amplitude && typeof amplitude.init === 'function') {
                    const initOptions = {
                        autocapture: { elementInteractions: true, ...(options?.autocapture || {}) },
                        ...options
                    } as Record<string, unknown>;
                    // remove our non-amplitude helper fields before passing through
                    delete (initOptions as any).apiKey;
                    delete (initOptions as any).sampleRate;
                    amplitude.init(apiKey, userId, initOptions);
                    // mark initialized and flush pending callbacks immediately after init
                    w.__amplitudeInitialized = true;
                    if (Array.isArray(w.__amplitudeOnReady)) {
                        const callbacks: Array<() => void> = w.__amplitudeOnReady.splice(0);
                        callbacks.forEach((cb) => {
                            try {
                                cb();
                            } catch {
                                // no-op
                            }
                        });
                    }
                }

                // Wait a bit after Amplitude init to ensure user properties are available
                // Then load engagement plugin and add it
                setTimeout(() => {
                    const engagementScript = document.createElement('script');
                    engagementScript.async = true;
                    engagementScript.src = engagementSrc;
                    engagementScript.onload = () => {
                        try {
                            const engagement = (w as any).engagement;
                            const amplitude = (w as any).amplitude;
                            if (engagement && amplitude && typeof amplitude.add === 'function') {
                                // Initialize engagement plugin
                                if (typeof engagement.plugin === 'function') {
                                    amplitude.add(engagement.plugin());
                                    // Register callback for guide actions using addCallback
                                    // The callback key "open_tidio_chat" should match what's configured in Amplitude
                                    if (typeof engagement.addCallback === 'function') {
                                        engagement.addCallback('open_tidio_chat', () => {
                                            // Open Tidio chat with predefined message when guide action is triggered
                                            openTidioChatWithMessage('I want to know ' +
                                                'which plan is best for me');
                                        });
                                    }
                                }
                            }
                            // engagement plugin loaded; already marked initialized after init
                        } catch {
                            // no-op
                        }
                    };
                    insertBeforeFirstScript(engagementScript);
                }, 500); // Wait 500ms after Amplitude init to ensure properties are available
            } catch {
                // no-op
            }
        };
        insertBeforeFirstScript(sessionScript);
    };
    analyticsScript.onerror = () => {
        try {
            console.error('[Amplitude] Failed to load Analytics SDK:', analyticsSrc);
        } catch {
            // no-op
        }
    };

    insertBeforeFirstScript(analyticsScript);
};

export default initializeAmplitude;

export const identifyAmplitudeUser = (userId?: string) => {
    const w = window as unknown as Record<string, any>;
    const amplitude = (w as any).amplitude;
    if (!amplitude) return;

    try {
        if (userId && typeof amplitude.setUserId === 'function') {
            amplitude.setUserId(userId);
        }
    } catch {
        // no-op
    }
};

export const trackAmplitudeEvent = (eventName: string, eventProperties?: Record<string, unknown>) => {
    const w = window as unknown as Record<string, any>;
    const call = () => {
        const amplitude = (w as any).amplitude;
        if (!amplitude || typeof amplitude.track !== 'function') return;
        try {
            amplitude.track(eventName, eventProperties || {});
        } catch {
            // no-op
        }
    };
    if (w.__amplitudeInitialized) {
        call();
    } else {
        w.__amplitudeOnReady = w.__amplitudeOnReady || [];
        w.__amplitudeOnReady.push(call);
    }
};

export const setAmplitudeUserProperties = (userProperties: Record<string, unknown>) => {
    const w = window as unknown as Record<string, any>;
    const call = () => {
        const amplitude = (w as any).amplitude;
        if (!amplitude) return;
        try {
            // Use Identify API to set user properties
            if (amplitude.Identify && typeof amplitude.Identify === 'function') {
                const identify = new amplitude.Identify();
                Object.keys(userProperties).forEach((key) => {
                    identify.set(key, userProperties[key]);
                });
                amplitude.identify(identify);
            } else if (typeof amplitude.setUserProperties === 'function') {
                amplitude.setUserProperties(userProperties);
            }
        } catch {
            // no-op
        }
    };
    if (w.__amplitudeInitialized) {
        call();
    } else {
        w.__amplitudeOnReady = w.__amplitudeOnReady || [];
        w.__amplitudeOnReady.push(call);
    }
};
